import {
  BadgeCheck,
  Car,
  Download,
  Edit,
  ExternalLink,
  MessageCircle,
  Plane,
  Share2,
  Trash2,
  User,
} from "lucide-react";
import { useNavigate } from "react-router";

import { handleDownloadPDF } from "./downloadPdf";

import { Button } from "../../../components/ui/button";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "../../../components/ui/sheet";
import { useNotification } from "../../../contexts/NotificationContext";

interface Props {
  open: boolean;
  booking: any;
  driver?: any;
  vehicle?: any;
  handleOpenModal: () => void;
  handleClose: () => void;
}

export default function BottomSheetModal({
  open,
  booking,
  driver,
  vehicle,
  handleOpenModal,
  handleClose,
}: Props) {
  const navigate = useNavigate();
  const { showNotification } = useNotification();

  const generateVoucherWhatsAppLink = (phoneNumber: string) => {
    const cleanNumber = phoneNumber.toString().replace(/\D/g, "");
    window.open(
      `https://wa.me/${cleanNumber}?text=https://www.transferonfloripa.com.br/voucher/${booking.id}`
    );
  };

  const generateWhatsAppLink = (phoneNumber: string) => {
    const cleanNumber = phoneNumber.toString().replace(/\D/g, "");
    window.open(`https://wa.me/${cleanNumber}`);
  };

  const generateDriverVoucherWhatsAppLink = (phoneNumber: string) => {
    const cleanNumber = phoneNumber.toString().replace(/\D/g, "");
    window.open(
      `https://wa.me/${cleanNumber}?text=https://www.transferonfloripa.com.br/driver-voucher/${booking.id}`
    );
  };

  const downloadPdf = () => {
    showNotification("info", "Baixando PDF...");
    handleDownloadPDF(booking, vehicle, driver);
  };

  const handleShare = () => {
    const payload = {
      title: "Acompanhe seu voucher!",
      url: `https://www.transferonfloripa.com.br/voucher/${booking.id}`,
    };

    if (navigator.share) {
      navigator
        .share(payload)
        .catch(() =>
          showNotification(
            "warning",
            "Não foi possível compartilhar o voucher."
          )
        );
    } else {
      showNotification(
        "warning",
        "Compartilhamento indisponível neste dispositivo."
      );
    }
  };

  return (
    <Sheet
      open={open}
      onOpenChange={(state) => (!state ? handleClose() : null)}
    >
      <SheetContent
        side="bottom"
        className="rounded-t-3xl border border-border"
      >
        <SheetHeader>
          <SheetTitle>Opções</SheetTitle>
          <SheetDescription>
            Compartilhe, acompanhe ou edite a reserva.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-2">
          {/* Ações principais */}
          <Button
            variant="default"
            className="w-full justify-start"
            onClick={() =>
              window.open(
                `https://www.transferonfloripa.com.br/voucher/${booking.id}`
              )
            }
          >
            <ExternalLink className="mr-2 h-4 w-4" /> Abrir voucher
          </Button>

          <Button
            variant="default"
            className="w-full justify-start"
            onClick={() =>
              window.open(
                `https://www.transferonfloripa.com.br/checkin/${booking.id}`
              )
            }
          >
            <BadgeCheck className="mr-2 h-4 w-4" /> Instruções de embarque
          </Button>

          {booking.phone && (
            <Button
              variant="default"
              className="w-full justify-start"
              onClick={() =>
                navigate(
                  `/admin-management/others/clients/${booking.phone
                    .toString()
                    .replace(/[^\d]/g, "")}`
                )
              }
            >
              <User className="mr-2 h-4 w-4" /> Ver cliente
            </Button>
          )}

          {/* Ações de comunicação */}
          {booking.phone && (
            <>
              <Button
                variant="outline"
                className="w-full justify-start border-emerald-500/50 text-emerald-600 hover:bg-emerald-50 hover:text-emerald-700"
                onClick={() => generateWhatsAppLink(booking.phone)}
              >
                <MessageCircle className="mr-2 h-4 w-4" /> WhatsApp
              </Button>

              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => generateVoucherWhatsAppLink(booking.phone)}
              >
                <MessageCircle className="mr-2 h-4 w-4" /> Enviar voucher por
                WhatsApp
              </Button>
            </>
          )}

          {driver && driver.phone && (
            <Button
              variant="outline"
              className="w-full justify-start border-blue-500/50 text-blue-600 hover:bg-blue-50 hover:text-blue-700"
              onClick={() => generateDriverVoucherWhatsAppLink(driver.phone)}
            >
              <Car className="mr-2 h-4 w-4" /> Enviar voucher ao Motorista
            </Button>
          )}

          <Button
            variant="outline"
            className="w-full justify-start"
            onClick={handleShare}
          >
            <Share2 className="mr-2 h-4 w-4" /> Compartilhar
          </Button>

          {/* Ações de acompanhamento */}
          {booking.flight && (
            <Button
              variant="outline"
              className="w-full justify-start"
              onClick={() =>
                window.open(`https://www.flightradar24.com/${booking.flight}`)
              }
            >
              <Plane className="mr-2 h-4 w-4" /> Acompanhar voo
            </Button>
          )}

          {/* Ações de documento */}
          <Button
            variant="outline"
            className="w-full justify-start"
            onClick={downloadPdf}
          >
            <Download className="mr-2 h-4 w-4" /> Baixar PDF
          </Button>

          {/* Ações de edição */}
          <Button
            variant="outline"
            className="w-full justify-start"
            onClick={() => navigate(`/admin-management/edit/${booking.id}`)}
          >
            <Edit className="mr-2 h-4 w-4" /> Editar
          </Button>

          {/* Ações destrutivas */}
          <Button
            variant="destructive"
            className="w-full justify-start"
            onClick={handleOpenModal}
          >
            <Trash2 className="mr-2 h-4 w-4" /> Excluir
          </Button>

          {/* Fechar */}
          <SheetClose asChild>
            <Button variant="ghost" className="w-full justify-center">
              Fechar
            </Button>
          </SheetClose>
        </div>
      </SheetContent>
    </Sheet>
  );
}
